import { useEffect, useRef, useState } from 'react';
import { Plus, Search, Edit3, Trash2, Mail, Phone, X, ArrowRight, UserCheck, UserPlus, ExternalLink, Snowflake, Sun, Flame, Building2, Activity } from 'lucide-react';
import { crmApi, Lead, Funil, Contato, EmpresaConta } from '../crmApi';
import EtiquetasPicker from '../EtiquetasPicker';
import CamposCustomForm from '../CamposCustomForm';
import EmpresaContaPicker from '../EmpresaContaPicker';
import ResponsavelPicker from '../ResponsavelPicker';
import CardDrawer from '../CardDrawer';

interface Props { session: any; }

const STATUS: { value: Lead['status']; label: string; cor: string }[] = [
  { value: 'novo', label: 'Novo', cor: 'bg-blue-50 text-blue-700' },
  { value: 'em_contato', label: 'Em contato', cor: 'bg-yellow-50 text-yellow-700' },
  { value: 'qualificado', label: 'Qualificado', cor: 'bg-green-50 text-green-700' },
  { value: 'nao_qualificado', label: 'Não qualificado', cor: 'bg-gray-100 text-gray-700' },
  { value: 'convertido', label: 'Convertido', cor: 'bg-purple-50 text-purple-700' },
];

const TEMPERATURAS: { value: NonNullable<Lead['temperatura']>; label: string; cor: string; Icon: any }[] = [
  { value: 'frio', label: 'Frio', cor: 'bg-sky-100 text-sky-700 border-sky-300', Icon: Snowflake },
  { value: 'morno', label: 'Morno', cor: 'bg-amber-100 text-amber-700 border-amber-300', Icon: Sun },
  { value: 'quente', label: 'Quente', cor: 'bg-red-100 text-red-700 border-red-300', Icon: Flame },
];

const CRMLeads = ({ session }: Props) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState<string>('');
  const [minhaCarteira, setMinhaCarteira] = useState(true);
  const [carregando, setCarregando] = useState(false);
  const [editando, setEditando] = useState<Lead | null>(null);
  const [novo, setNovo] = useState(false);
  const [convertendo, setConvertendo] = useState<Lead | null>(null);
  const [drawerLead, setDrawerLead] = useState<Lead | null>(null);

  const carregar = async () => {
    setCarregando(true);
    try { setLeads(await crmApi.listarLeads(session)); }
    catch (e: any) { alert('Erro: ' + e.message); }
    finally { setCarregando(false); }
  };
  useEffect(() => { carregar(); }, []); // eslint-disable-line

  const meuEmail = session?.usuario?.email || '';
  const filtrados = leads.filter(l => {
    const q = busca.toLowerCase().trim();
    const matchQ = !q || l.nome.toLowerCase().includes(q) || (l.email || '').toLowerCase().includes(q) || (l.empresa || '').toLowerCase().includes(q);
    const matchS = !filtroStatus || l.status === filtroStatus;
    const matchC = !minhaCarteira || (l.responsavelId === meuEmail);
    return matchQ && matchS && matchC;
  });

  const remover = async (l: Lead) => {
    if (!confirm(`Excluir lead "${l.nome}"?`)) return;
    await crmApi.removerLead(session, l.id);
    carregar();
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Leads</h1>
          <p className="text-sm text-gray-600">Capture, qualifique e converta seus leads em oportunidades</p>
        </div>
        <button onClick={() => setNovo(true)} className="bg-black text-white px-3 py-2 rounded-lg flex items-center gap-1.5 hover:bg-gray-800 text-sm">
          <Plus className="h-4 w-4" /> Novo Lead
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar leads…"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </div>
        <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white">
          <option value="">Todos os status</option>
          {STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          <button onClick={() => setMinhaCarteira(true)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${minhaCarteira ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'}`}>
            Minha carteira
          </button>
          <button onClick={() => setMinhaCarteira(false)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${!minhaCarteira ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-800'}`}>
            Toda a equipe
          </button>
        </div>
      </div>

      {carregando && <div className="text-sm text-gray-500">Carregando…</div>}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="text-xs text-gray-600 uppercase">
              <th className="text-left px-4 py-2">Nome</th>
              <th className="text-left px-4 py-2 hidden md:table-cell">Empresa</th>
              <th className="text-left px-4 py-2 hidden sm:table-cell">Contato</th>
              <th className="text-left px-4 py-2">Origem</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="px-4 py-2 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {filtrados.length === 0 && !carregando && (
              <tr><td colSpan={6} className="text-center py-12 text-gray-500 text-sm">Nenhum lead encontrado.</td></tr>
            )}
            {filtrados.map(l => {
              const st = STATUS.find(s => s.value === l.status) || STATUS[0];
              return (
                <tr key={l.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2">
                    <button onClick={() => setEditando(l)} className="font-medium text-gray-900 hover:underline text-left">{l.nome}</button>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-700 hidden md:table-cell">{l.empresa || '—'}</td>
                  <td className="px-4 py-2 text-xs text-gray-600 hidden sm:table-cell">
                    {l.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" /> {l.email}</div>}
                    {l.telefone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" /> {l.telefone}</div>}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-600">{l.origem || '—'}</td>
                  <td className="px-4 py-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${st.cor}`}>{st.label}</span>
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setDrawerLead(l)} title="Atividades / WhatsApp"
                      className="p-1 hover:bg-blue-50 rounded text-blue-700 mr-1"><Activity className="h-4 w-4" /></button>
                    {l.status !== 'convertido' && (
                      <button onClick={() => setConvertendo(l)} title="Converter em oportunidade"
                        className="p-1 hover:bg-green-50 rounded text-green-700"><ArrowRight className="h-4 w-4" /></button>
                    )}
                    <button onClick={() => setEditando(l)} className="p-1 hover:bg-gray-100 rounded text-gray-600 ml-1"><Edit3 className="h-4 w-4" /></button>
                    <button onClick={() => remover(l)} className="p-1 hover:bg-red-50 rounded text-red-600 ml-1"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {(novo || editando) && (
        <LeadModal session={session} lead={editando || undefined}
          onClose={() => { setNovo(false); setEditando(null); }}
          onSaved={() => { setNovo(false); setEditando(null); carregar(); }} />
      )}

      {convertendo && (
        <ConverterModal session={session} lead={convertendo}
          onClose={() => setConvertendo(null)}
          onConverted={() => { setConvertendo(null); carregar(); }} />
      )}

      <CardDrawer session={session} open={!!drawerLead} onClose={() => setDrawerLead(null)}
        vinculo={drawerLead ? { leadId: drawerLead.id } : {}}
        titulo={drawerLead?.nome || ''}
        subtitulo={drawerLead?.empresa || drawerLead?.email || ''}
        telefone={drawerLead?.telefone}
        responsavelId={drawerLead?.responsavelId} />
    </div>
  );
};

const LeadModal = ({ session, lead, onClose, onSaved }: { session: any; lead?: Lead; onClose: () => void; onSaved: () => void }) => {
  // Dados do form
  const [nome, setNome] = useState(lead?.nome || '');
  const [email, setEmail] = useState(lead?.email || '');
  const [telefone, setTelefone] = useState(lead?.telefone || '');
  const [empresa, setEmpresa] = useState(lead?.empresa || '');
  const [origem, setOrigem] = useState(lead?.origem || '');
  const [status, setStatus] = useState<Lead['status']>(lead?.status || 'novo');
  const [temperatura, setTemperatura] = useState<NonNullable<Lead['temperatura']>>(lead?.temperatura || 'morno');
  const [responsavelId, setResponsavelId] = useState<string | null>(lead?.responsavelId || (session?.usuario?.email || null));
  const [observacoes, setObservacoes] = useState(lead?.observacoes || '');
  const [etiquetas, setEtiquetas] = useState<string[]>(lead?.etiquetas || []);
  const [camposCustom, setCamposCustom] = useState<Record<string, any>>(lead?.camposCustom || {});
  const [salvando, setSalvando] = useState(false);

  // Busca de contatos existentes
  const [contatos, setContatos] = useState<Contato[]>([]);
  const [sugestoes, setSugestoes] = useState<Contato[]>([]);
  const [contatoVinculado, setContatoVinculado] = useState<Contato | null>(null);
  const [dropdownAberto, setDropdownAberto] = useState(false);
  const [criarContato, setCriarContato] = useState(false);
  const nomeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    crmApi.listarContatos(session).then(setContatos).catch(() => {});
  }, []); // eslint-disable-line

  // Fecha dropdown ao clicar fora
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (nomeRef.current && !nomeRef.current.contains(e.target as Node)) setDropdownAberto(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const handleNomeChange = (v: string) => {
    setNome(v);
    if (contatoVinculado) setContatoVinculado(null); // desvincular se editar manualmente
    if (v.trim().length < 2) { setSugestoes([]); setDropdownAberto(false); return; }
    const q = v.toLowerCase();
    const matches = contatos.filter(c =>
      c.nome.toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.empresa || '').toLowerCase().includes(q)
    ).slice(0, 6);
    setSugestoes(matches);
    setDropdownAberto(matches.length > 0);
  };

  const vincularContato = (c: Contato) => {
    setContatoVinculado(c);
    setNome(c.nome);
    setEmail(c.email || '');
    setTelefone(c.telefone || '');
    setEmpresa(c.empresa || '');
    setSugestoes([]);
    setDropdownAberto(false);
  };

  const desvincular = () => {
    setContatoVinculado(null);
  };

  const salvar = async () => {
    if (!nome.trim()) { alert('Nome obrigatório'); return; }
    setSalvando(true);
    try {
      const payload = { nome: nome.trim(), email, telefone, empresa, origem, status, temperatura, responsavelId, observacoes, etiquetas, camposCustom };
      if (lead) {
        await crmApi.atualizarLead(session, lead.id, payload);
      } else {
        await crmApi.criarLead(session, payload);
        // Criar também como Contato se solicitado e não vinculado
        if (criarContato && !contatoVinculado) {
          try {
            await crmApi.criarContato(session, { nome: nome.trim(), email, telefone, empresa, tipo: 'prospect', etiquetas });
          } catch { /* silencioso — lead já foi salvo */ }
        }
      }
      onSaved();
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-gray-900">{lead ? 'Editar Lead' : 'Novo Lead'}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-5 space-y-3">
          {/* ── Campo nome com busca inteligente ── */}
          <div ref={nomeRef} className="relative">
            <label className="text-xs text-gray-600 flex items-center gap-1">
              Nome *
              {contatoVinculado && (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded-full">
                  <UserCheck className="h-2.5 w-2.5" /> Contato existente
                  <button onClick={desvincular} className="ml-0.5 hover:text-red-500"><X className="h-2.5 w-2.5" /></button>
                </span>
              )}
            </label>
            <input
              value={nome}
              onChange={e => handleNomeChange(e.target.value)}
              onFocus={() => sugestoes.length > 0 && setDropdownAberto(true)}
              autoFocus
              placeholder="Digite o nome ou e-mail para buscar…"
              className={`w-full mt-1 px-3 py-2 border rounded-lg text-sm ${contatoVinculado ? 'border-green-400 bg-green-50' : 'border-gray-300'}`}
            />
            {/* Dropdown de sugestões */}
            {dropdownAberto && sugestoes.length > 0 && (
              <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                <p className="text-[10px] text-gray-400 px-3 pt-2 pb-1 uppercase tracking-wide">Contatos cadastrados</p>
                {sugestoes.map(c => (
                  <button key={c.id} type="button" onClick={() => vincularContato(c)}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 text-left transition-colors">
                    <div className="h-7 w-7 rounded-full bg-gray-200 flex items-center justify-center text-[11px] font-semibold text-gray-600 flex-shrink-0">
                      {c.nome.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{c.nome}</p>
                      <p className="text-[11px] text-gray-500 truncate">
                        {[c.empresa, c.email, c.telefone].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <span className="text-[10px] text-blue-600 flex-shrink-0">Usar dados →</span>
                  </button>
                ))}
                <div className="border-t border-gray-100 px-3 py-2">
                  <p className="text-[11px] text-gray-400 flex items-center gap-1">
                    <UserPlus className="h-3 w-3" /> Não encontrou? Continue digitando para cadastrar novo.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Linha 2: E-mail | Telefone | Empresa */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-600">E-mail</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Telefone</label>
              <input value={telefone} onChange={e => setTelefone(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Empresa</label>
              <input value={empresa} onChange={e => setEmpresa(e.target.value)}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>

          {/* Linha 3: Origem | Status */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">Origem</label>
              <input value={origem} onChange={e => setOrigem(e.target.value)} placeholder="Site, Anúncio, Indicação…"
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-gray-600">Status</label>
              <select value={status} onChange={e => setStatus(e.target.value as Lead['status'])}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                {STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
          </div>

          {/* Temperatura */}
          <div>
            <label className="text-xs text-gray-600">Temperatura</label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {TEMPERATURAS.map(t => {
                const ativo = temperatura === t.value;
                return (
                  <button key={t.value} type="button" onClick={() => setTemperatura(t.value)}
                    className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors ${ativo ? t.cor : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                    <t.Icon className="h-4 w-4" /> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Responsável */}
          <ResponsavelPicker session={session} value={responsavelId} onChange={setResponsavelId} />

          {/* Linha 4: Observações + Etiquetas lado a lado */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-600">Observações</label>
              <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={2}
                className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <EtiquetasPicker session={session} value={etiquetas} onChange={setEtiquetas} />
            </div>
          </div>

          <CamposCustomForm session={session} entidade="lead" value={camposCustom} onChange={setCamposCustom} />

          {/* Opção de criar contato — só para novos leads não vinculados */}
          {!lead && (
            <div className={`rounded-lg border p-3 transition-colors ${contatoVinculado ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}>
              {contatoVinculado ? (
                <div className="flex items-center gap-2 text-xs text-green-700">
                  <UserCheck className="h-3.5 w-3.5" />
                  <span>Dados preenchidos do contato <strong>{contatoVinculado.nome}</strong></span>
                  <a href="#" onClick={e => { e.preventDefault(); }} className="ml-auto flex items-center gap-0.5 text-blue-600 hover:underline">
                    <ExternalLink className="h-3 w-3" /> Ver contato
                  </a>
                </div>
              ) : (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={criarContato} onChange={e => setCriarContato(e.target.checked)}
                    className="rounded border-gray-300 text-black" />
                  <div>
                    <p className="text-xs font-medium text-gray-800 flex items-center gap-1">
                      <UserPlus className="h-3.5 w-3.5" /> Cadastrar também em Contatos
                    </p>
                    <p className="text-[11px] text-gray-500">Cria um contato com esses dados ao salvar o lead</p>
                  </div>
                </label>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-700">Cancelar</button>
          <button onClick={salvar} disabled={salvando}
            className="px-4 py-1.5 bg-black text-white rounded-lg text-sm hover:bg-gray-800 disabled:opacity-50">
            {salvando ? 'Salvando...' : (criarContato && !contatoVinculado && !lead ? 'Salvar + Criar Contato' : 'Salvar')}
          </button>
        </div>
      </div>
    </div>
  );
};

const ConverterModal = ({ session, lead, onClose, onConverted }: { session: any; lead: Lead; onClose: () => void; onConverted: () => void }) => {
  const [funis, setFunis] = useState<Funil[]>([]);
  const [empresasConta, setEmpresasConta] = useState<EmpresaConta[]>([]);
  const [empresaContaId, setEmpresaContaId] = useState<string | null>(null);
  const [criarEmpresa, setCriarEmpresa] = useState(false);
  const [novaEmpresa, setNovaEmpresa] = useState({
    razaoSocial: lead.empresa || '',
    nomeFantasia: '',
    cnpj: '',
    ramo: '',
  });

  const [criarOportunidade, setCriarOportunidade] = useState(true);
  const [funilId, setFunilId] = useState('');
  const [etapaId, setEtapaId] = useState('');
  const [titulo, setTitulo] = useState(`Oportunidade — ${lead.nome}`);
  const [valor, setValor] = useState('');

  const [cargo, setCargo] = useState('');
  const [tipoContato, setTipoContato] = useState<Contato['tipo']>('cliente');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    crmApi.listarFunis(session).then(fs => {
      setFunis(fs);
      if (fs.length > 0) {
        setFunilId(fs[0].id);
        setEtapaId(fs[0].etapas[0]?.id || '');
      } else {
        setCriarOportunidade(false);
      }
    });
    crmApi.listarEmpresasConta(session).then(setEmpresasConta).catch(() => {});
  }, []); // eslint-disable-line

  // Auto-sugerir empresa pela razão social do lead
  useEffect(() => {
    if (!lead.empresa || empresaContaId) return;
    const match = empresasConta.find(e =>
      e.razaoSocial.toLowerCase() === (lead.empresa || '').toLowerCase() ||
      (e.nomeFantasia || '').toLowerCase() === (lead.empresa || '').toLowerCase()
    );
    if (match) setEmpresaContaId(match.id);
  }, [empresasConta]); // eslint-disable-line

  const funilSel = funis.find(f => f.id === funilId);

  const converter = async () => {
    if (criarOportunidade && (!funilId || !etapaId)) { alert('Selecione funil e etapa'); return; }
    setSalvando(true);
    try {
      await crmApi.converterLead(session, lead.id, {
        empresaContaId: empresaContaId || undefined,
        criarEmpresa: !empresaContaId && criarEmpresa,
        empresaContaNova: !empresaContaId && criarEmpresa ? novaEmpresa : undefined,
        cargo,
        tipoContato,
        criarOportunidade,
        oportunidade: criarOportunidade ? {
          funilId, etapaId,
          titulo,
          valor: parseFloat(valor.replace(',', '.')) || 0,
        } : undefined,
      });
      onConverted();
    } catch (e: any) { alert('Erro: ' + e.message); }
    finally { setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-gray-900 flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-green-600" /> Converter Lead
          </h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
            Lead: <strong>{lead.nome}</strong>
            {lead.empresa && <span className="text-gray-500"> · {lead.empresa}</span>}
          </div>

          {/* Passo 1: Empresa */}
          <div className="border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
              <Building2 className="h-3.5 w-3.5" /> 1. Empresa (Conta)
            </p>
            <EmpresaContaPicker session={session} value={empresaContaId} onChange={(id) => { setEmpresaContaId(id); if (id) setCriarEmpresa(false); }} label="Selecionar empresa existente" placeholder="Buscar pelo nome ou CNPJ…" />
            {!empresaContaId && (
              <div className="mt-2">
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input type="checkbox" checked={criarEmpresa} onChange={e => setCriarEmpresa(e.target.checked)} />
                  Cadastrar nova empresa
                </label>
                {criarEmpresa && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                      <label className="text-[11px] text-gray-500">Razão Social *</label>
                      <input value={novaEmpresa.razaoSocial} onChange={e => setNovaEmpresa({ ...novaEmpresa, razaoSocial: e.target.value })}
                        className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded text-sm" />
                    </div>
                    <div>
                      <label className="text-[11px] text-gray-500">Nome Fantasia</label>
                      <input value={novaEmpresa.nomeFantasia} onChange={e => setNovaEmpresa({ ...novaEmpresa, nomeFantasia: e.target.value })}
                        className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded text-sm" />
                    </div>
                    <div>
                      <label className="text-[11px] text-gray-500">CNPJ</label>
                      <input value={novaEmpresa.cnpj} onChange={e => setNovaEmpresa({ ...novaEmpresa, cnpj: e.target.value })}
                        className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded text-sm" />
                    </div>
                    <div className="col-span-2">
                      <label className="text-[11px] text-gray-500">Ramo</label>
                      <input value={novaEmpresa.ramo} onChange={e => setNovaEmpresa({ ...novaEmpresa, ramo: e.target.value })}
                        className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded text-sm" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Passo 2: Contato */}
          <div className="border border-gray-200 rounded-lg p-3">
            <p className="text-xs font-semibold text-gray-700 mb-2 flex items-center gap-1">
              <UserCheck className="h-3.5 w-3.5" /> 2. Contato (será criado automaticamente)
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] text-gray-500">Cargo</label>
                <input value={cargo} onChange={e => setCargo(e.target.value)}
                  className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded text-sm" />
              </div>
              <div>
                <label className="text-[11px] text-gray-500">Tipo</label>
                <select value={tipoContato} onChange={e => setTipoContato(e.target.value as Contato['tipo'])}
                  className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded text-sm bg-white">
                  <option value="cliente">Cliente</option>
                  <option value="prospect">Prospect</option>
                  <option value="parceiro">Parceiro</option>
                  <option value="fornecedor">Fornecedor</option>
                </select>
              </div>
            </div>
          </div>

          {/* Passo 3: Oportunidade */}
          <div className="border border-gray-200 rounded-lg p-3">
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 mb-2">
              <input type="checkbox" checked={criarOportunidade} onChange={e => setCriarOportunidade(e.target.checked)} disabled={funis.length === 0} />
              <ArrowRight className="h-3.5 w-3.5" /> 3. Criar Oportunidade {funis.length === 0 && <span className="text-gray-400 font-normal">(crie um funil primeiro)</span>}
            </label>
            {criarOportunidade && funis.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] text-gray-500">Funil</label>
                    <select value={funilId} onChange={e => { setFunilId(e.target.value); const f = funis.find(x => x.id === e.target.value); setEtapaId(f?.etapas[0]?.id || ''); }}
                      className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded text-sm bg-white">
                      {funis.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-gray-500">Etapa inicial</label>
                    <select value={etapaId} onChange={e => setEtapaId(e.target.value)}
                      className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded text-sm bg-white">
                      {funilSel?.etapas.map(et => <option key={et.id} value={et.id}>{et.nome}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] text-gray-500">Título</label>
                  <input value={titulo} onChange={e => setTitulo(e.target.value)}
                    className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="text-[11px] text-gray-500">Valor (R$)</label>
                  <input value={valor} onChange={e => setValor(e.target.value)} inputMode="decimal" placeholder="0,00"
                    className="w-full mt-0.5 px-2 py-1.5 border border-gray-300 rounded text-sm" />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 pb-5">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-700">Cancelar</button>
          <button onClick={converter} disabled={salvando}
            className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-1">
            {salvando ? 'Convertendo...' : <><ArrowRight className="h-3.5 w-3.5" /> Converter</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CRMLeads;
